import { Box, Button, Slider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface IProps {
  priceAdjustment: number;
  costAdjustment: number;
  modifiersAdjustment: number;
  commissionAdjustment: number;
  onPriceChange: (value: number) => void;
  onCostChange: (value: number) => void;
  onModifiersChange: (value: number) => void;
  onCommissionChange: (value: number) => void;
  onReset: () => void;
}

const sliderSx = {
  color: "#FF8D3F",
  "& .MuiSlider-thumb": {
    bgcolor: "#FFFFFF",
    border: "2px solid #FF8D3F",
    width: 16,
    height: 16,
  },
  "& .MuiSlider-track": {
    bgcolor: "#FF8D3F",
  },
  "& .MuiSlider-rail": {
    bgcolor: "#EAECF0",
  },
};

const LiveControls = ({
  priceAdjustment,
  costAdjustment,
  modifiersAdjustment,
  commissionAdjustment,
  onPriceChange,
  onCostChange,
  onModifiersChange,
  onCommissionChange,
  onReset,
}: IProps) => {
  const { t } = useTranslation("aggregators");

  return (
    <Box
      p={"20px"}
      border={"1px solid #EAECF0"}
      borderRadius={1}
      bgcolor={"#FFFFFF"}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        mb={3}
      >
        <Typography fontSize={"16px"} fontWeight={600} color={"#101828"}>
          {t("liveControls")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onReset}
          sx={{
            color: "#667085",
            borderColor: "#EAECF0",
            "&:hover": {
              borderColor: "#D0D5DD",
              bgcolor: "#F9FAFB",
            },
          }}
        >
          {t("resetToOriginal")}
        </Button>
      </Stack>

      <Stack spacing={3}>
        <Box>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            mb={1}
          >
            <Typography fontSize={"14px"} color={"#344054"}>
              {t("priceAdjustment")}
            </Typography>
            <Typography fontSize={"14px"} fontWeight={600} color={"#101828"}>
              {priceAdjustment > 0 ? "+" : ""}
              {priceAdjustment}%
            </Typography>
          </Stack>
          <Slider
            value={priceAdjustment}
            onChange={(_, val) => onPriceChange(val as number)}
            min={-30}
            max={30}
            sx={sliderSx}
          />
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              -30
            </Typography>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              +30
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            mb={1}
          >
            <Typography fontSize={"14px"} color={"#344054"}>
              {t("productCostAdjustment")}
            </Typography>
            <Typography fontSize={"14px"} fontWeight={600} color={"#101828"}>
              {costAdjustment > 0 ? "+" : ""}
              {costAdjustment}%
            </Typography>
          </Stack>
          <Slider
            value={costAdjustment}
            onChange={(_, val) => onCostChange(val as number)}
            min={-30}
            max={30}
            sx={sliderSx}
          />
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              -30
            </Typography>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              +30
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            mb={1}
          >
            <Typography fontSize={"14px"} color={"#344054"}>
              {t("modifiersCostAdjustment")}
            </Typography>
            <Typography fontSize={"14px"} fontWeight={600} color={"#101828"}>
              {modifiersAdjustment > 0 ? "+" : ""}
              {modifiersAdjustment}%
            </Typography>
          </Stack>
          <Slider
            value={modifiersAdjustment}
            onChange={(_, val) => onModifiersChange(val as number)}
            min={-30}
            max={30}
            sx={sliderSx}
          />
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              -30
            </Typography>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              +30
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            mb={1}
          >
            <Typography fontSize={"14px"} color={"#344054"}>
              {t("aggregatorCommission")}
            </Typography>
            <Typography fontSize={"14px"} fontWeight={600} color={"#101828"}>
              {commissionAdjustment > 0 ? "+" : ""}
              {commissionAdjustment}%
            </Typography>
          </Stack>
          <Slider
            value={commissionAdjustment}
            onChange={(_, val) => onCommissionChange(val as number)}
            min={-30}
            max={30}
            sx={sliderSx}
          />
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              -30
            </Typography>
            <Typography fontSize={"12px"} color={"#98A2B3"}>
              +30
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default LiveControls;
